import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Sparkles,
  Play,
  Award,
  Users,
  BookOpen,
  Star,
  Check,
  ShieldCheck,
  Building2,
  Loader2,
  Send,
  Target,
  Zap,
  Clock,
  Lock,
  Globe2,
  Radar,
  TrendingUp,
} from "lucide-react";
import { courses } from "../data/mockData";
import { LOGO_URL } from "../constants";
import { api } from "../api/client";
import { toast } from "sonner";

// Scroll-reveal wrapper (IntersectionObserver based)
function Reveal({ children, delay = 0, as: Tag = "div", className = "" }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setVisible(true);
          io.disconnect();
        }
      },
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <Tag
      ref={ref}
      style={{ transitionDelay: `${delay}ms` }}
      className={`transition-all duration-700 ease-out will-change-transform ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"} ${className}`}
    >
      {children}
    </Tag>
  );
}

const NIGE_AVATAR = "/nige.png";

const PEDIGREE = [
  { label: "Ex-SAS · Close Protection", icon: ShieldCheck },
  { label: "Former MI5 · Surveillance", icon: Radar },
  { label: "CISO Advisor · CISSP", icon: Lock },
  { label: "UN/UNDSS · HEAT", icon: Globe2 },
];

const TRUSTED_BY = [
  "Fortune 500 Security Teams",
  "UN Agencies",
  "Global NGOs",
  "Private Maritime Ops",
  "Diplomatic Protection Units",
  "Major Defence Primes",
];

const OUTCOMES = [
  {
    big: "97%",
    small: "pass their first assessment",
    sub: "across Close Protection and HEAT cohorts",
  },
  {
    big: "2.4×",
    small: "faster time-to-deployment",
    sub: "vs. traditional classroom-only training",
  },
  {
    big: "<14d",
    small: "average certification window",
    sub: "for Counter-Terrorism Awareness",
  },
];

const STEPS = [
  {
    n: "01",
    title: "Learn from operators, not academics",
    body: "Every module is taught by someone who's done it. Live AAR-style debriefs replace dry lectures.",
    icon: Target,
  },
  {
    n: "02",
    title: "Prove it under pressure",
    body: "Interactive drills, scenario quizzes and timed assessments. No passive watch-and-forget content.",
    icon: Zap,
  },
  {
    n: "03",
    title: "Certify & deploy",
    body: "Download a verifiable certificate the moment you pass. Recognised by 600+ partner organisations.",
    icon: Award,
  },
];

const FAQ = [
  {
    q: "Are the courses CPD / BTEC accredited?",
    a: "Yes. Close Protection, HEAT, and Conflict Management modules are BTEC Level 3 aligned. ISO 27001 Lead Implementer follows the 2022 standard.",
  },
  {
    q: "How long do I have access?",
    a: "Lifetime access to every course you enrol in, including all future updates to the curriculum.",
  },
  {
    q: "Can my company buy seats in bulk?",
    a: "Absolutely — corporate licences start at 10 seats with volume pricing. Use the Corporate Training form below or email sales@bluemountain.io.",
  },
  {
    q: "Do you offer on-site delivery?",
    a: "Yes. BMG delivers live instructor-led training across EMEA and APAC. Typical lead time: 3–4 weeks.",
  },
];

export default function Landing() {
  const featured = courses.slice(0, 4);
  const [lead, setLead] = useState({
    company: "",
    name: "",
    email: "",
    phone: "",
    cohort_size: 10,
    interest: "Close Protection",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);

  const submitLead = async (e) => {
    e.preventDefault();
    if (!lead.company || !lead.name || !lead.email) {
      toast.error("Company, name and email are required");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/corporate-leads", lead);
      setSubmitted(true);
      toast.success("Request received — we'll be in touch within 24h");
    } catch {
      toast.error("Couldn't send. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink-900 text-white">
      {/* Top Nav */}
      <nav className="sticky top-0 z-50 bg-ink-900/75 backdrop-blur-md border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2.5"
            data-testid="nav-brand"
          >
            <img
              src={LOGO_URL}
              alt="Blue Mountain Group"
              className="h-12 w-auto object-contain"
            />
          </Link>
          <div className="hidden md:flex items-center gap-7 text-sm text-gray-300">
            <a
              href="#top"
              data-testid="nav-home"
              className="hover:text-white transition-colors"
            >
              Home
            </a>
            <a href="#how" className="hover:text-white transition-colors">
              How it works
            </a>
            <a href="#courses" className="hover:text-white transition-colors">
              Courses
            </a>
            <a
              href="#instructors"
              className="hover:text-white transition-colors"
            >
              Instructors
            </a>
            <a href="#corporate" className="hover:text-white transition-colors">
              Corporate
            </a>
            <a href="#faq" className="hover:text-white transition-colors">
              FAQ
            </a>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/dashboard"
              data-testid="nav-enter"
              className="px-5 py-2.5 rounded-full bg-lime text-ink-900 text-sm font-bold hover:bg-lime-hover transition-all hover:-translate-y-0.5"
            >
              Enter App
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section id="top" className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-grape/25 blur-[120px]" />
          <div className="absolute top-10 right-0 w-[420px] h-[420px] rounded-full bg-lime/15 blur-[120px]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-24 lg:pt-28 lg:pb-28">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-grape/15 border border-grape/30 text-grape text-xs font-semibold mb-7 fade-up">
            <Sparkles className="w-3.5 h-3.5" /> The UK&apos;s most trusted
            security-training platform
          </div>
          <h1
            className="font-display font-black text-5xl sm:text-6xl lg:text-8xl tracking-tighter leading-[0.92] max-w-5xl fade-up"
            style={{ animationDelay: "60ms" }}
          >
            Train for the <span className="text-lime">moment</span> everything
            goes wrong.
          </h1>
          <p
            className="mt-7 max-w-2xl text-lg text-gray-400 leading-relaxed fade-up"
            style={{ animationDelay: "140ms" }}
          >
            Elite security courses — Close Protection, Cybersecurity, HEAT,
            Maritime, Risk Advisory — taught by operators who&apos;ve actually
            been in the room. Watch, drill, assess, certify.
          </p>
          <div
            className="mt-9 flex flex-wrap items-center gap-4 fade-up"
            style={{ animationDelay: "220ms" }}
          >
            <Link
              to="/dashboard"
              data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-2 px-7 py-4 rounded-full bg-lime text-ink-900 font-bold hover:bg-lime-hover transition-all hover:-translate-y-0.5"
            >
              Start Training{" "}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/catalog"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-2 px-7 py-4 rounded-full border border-white/15 hover:border-white/25 hover:bg-white/5 font-semibold transition-colors"
            >
              <Play className="w-4 h-4" /> Browse 24 courses
            </Link>
          </div>

          {/* Stat strip */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 lg:gap-10">
            {[
              { num: "12,482", label: "Operators trained" },
              { num: "24", label: "Elite courses" },
              { num: "97%", label: "First-time pass rate" },
              { num: "4.8★", label: "Learner rating" },
            ].map((s, i) => (
              <Reveal
                key={i}
                delay={i * 60}
                className="border-l border-white/10 pl-6"
              >
                <div className="font-display font-black text-3xl lg:text-4xl">
                  {s.num}
                </div>
                <div className="text-sm text-gray-500 mt-1">{s.label}</div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Trusted by strip */}
      <section className="border-y border-white/5 bg-ink-800/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
          <Reveal>
            <div className="text-center text-xs uppercase tracking-[0.25em] text-gray-500 font-semibold mb-6">
              Trusted by security leaders worldwide
            </div>
          </Reveal>
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-sm text-gray-400">
            {TRUSTED_BY.map((t, i) => (
              <Reveal key={t} delay={i * 50}>
                <span className="font-display font-semibold tracking-tight">
                  {t}
                </span>
              </Reveal>
            ))}
          </div>

          {/* Accreditations & Compliance logos */}
          <Reveal>
            <div className="mt-12 mb-2 text-center text-[10px] uppercase tracking-[0.3em] text-gray-600 font-semibold">
              Accreditations & Compliance
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div
              data-testid="accreditations-strip"
              className="mt-4 rounded-3xl border border-white/5 px-4 sm:px-8 py-6 overflow-hidden"
              style={{backgroundColor: "#101115"}}
            >
              <img
                src="/img_3s0rsvdf.png"
                alt="Cyber Essentials Plus, JOSCAR, ISO 9001, ELC 5027, CTP Preferred Supplier, Highfield, Qualsafe accreditations"
                className="w-full h-auto object-contain max-h-28 sm:max-h-32 md:max-h-36 mx-auto select-none opacity-90"
                style={{
                  filter: "invert(1) hue-rotate(180deg) saturate(1.15) brightness(1.1)",
                  mixBlendMode: "screen",
                }}
                draggable={false}
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* How it works */}
      <section
        id="how"
        className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28"
      >
        <Reveal>
          <div className="max-w-3xl">
            <span className="text-xs uppercase tracking-widest text-lime font-semibold">
              How it works
            </span>
            <h2 className="font-display font-black text-4xl lg:text-6xl tracking-tighter mt-3 leading-[1]">
              Three moves. <span className="text-grape">Zero fluff.</span>
            </h2>
            <p className="mt-5 text-lg text-gray-400 max-w-2xl leading-relaxed">
              We stripped the LMS back to what actually moves the needle for
              security professionals. Learn, prove, certify — in that order.
            </p>
          </div>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            return (
              <Reveal key={s.n} delay={i * 120}>
                <div className="bg-ink-700 border border-white/5 rounded-3xl p-8 h-full relative overflow-hidden group hover:border-white/15 transition-all">
                  <div className="absolute -top-4 -right-4 font-display font-black text-[120px] leading-none text-white/[0.03] pointer-events-none">
                    {s.n}
                  </div>
                  <div className="relative">
                    <Icon
                      className={`w-8 h-8 ${i === 0 ? "text-lime" : i === 1 ? "text-grape" : "text-lime"} mb-5`}
                    />
                    <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold">
                      Step {s.n}
                    </div>
                    <h3 className="font-display font-bold text-2xl mt-2 leading-tight">
                      {s.title}
                    </h3>
                    <p className="text-sm text-gray-400 mt-3 leading-relaxed">
                      {s.body}
                    </p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* Features bento */}
      <section
        id="features"
        className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24"
      >
        <Reveal>
          <div className="max-w-2xl mb-12">
            <span className="text-xs uppercase tracking-widest text-lime font-semibold">
              Why Blue Mountain
            </span>
            <h2 className="font-display font-black text-4xl lg:text-5xl mt-3 leading-tight tracking-tight">
              Everything you need to{" "}
              <em className="not-italic text-grape">actually</em> finish.
            </h2>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <Reveal delay={0} className="md:row-span-2 md:col-span-1">
            <div className="bg-ink-700 border border-white/5 rounded-3xl p-8 relative overflow-hidden flex flex-col justify-between min-h-[360px] h-full">
              <div className="absolute -bottom-12 -right-12 w-48 h-48 rounded-full bg-lime/10 blur-3xl" />
              <div className="relative">
                <Play className="w-8 h-8 text-lime mb-5" />
                <div className="font-display text-2xl font-bold leading-tight">
                  Watch &amp; learn at <br />
                  your own pace
                </div>
                <p className="text-sm text-gray-400 mt-3 leading-relaxed">
                  HD lessons, live drill debriefs, bookmarks and full
                  transcripts. No fluff, no filler — every minute earns its
                  place.
                </p>
              </div>
              <div className="relative mt-6 aspect-video rounded-2xl bg-ink-900 border border-white/10 flex items-center justify-center" style={{backgroundImage: "url(/nige.png)", backgroundSize: "cover", backgroundPosition: "center"}}>
                <div className="w-14 h-14 rounded-full bg-lime flex items-center justify-center pulse-glow">
                  <Play
                    className="w-5 h-5 text-ink-900 ml-1"
                    fill="currentColor"
                  />
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={120}>
            <div className="bg-ink-700 border border-white/5 rounded-3xl p-8 min-h-[170px] h-full">
              <Check className="w-7 h-7 text-grape mb-4" />
              <div className="font-display text-xl font-bold">
                Scenario quizzes
              </div>
              <p className="text-sm text-gray-400 mt-2">
                Every module locks in with a scenario-based quiz. Miss a
                question, see the reasoning, retake instantly.
              </p>
            </div>
          </Reveal>

          <Reveal delay={180}>
            <div className="bg-ink-700 border border-white/5 rounded-3xl p-8 min-h-[170px] h-full">
              <Award className="w-7 h-7 text-lime mb-4" />
              <div className="font-display text-xl font-bold">
                Verifiable certificates
              </div>
              <p className="text-sm text-gray-400 mt-2">
                Printable, shareable, checked by a public verification URL.
                Recognised across 600+ partner organisations.
              </p>
            </div>
          </Reveal>

          <Reveal delay={240}>
            <div className="bg-ink-700 border border-white/5 rounded-3xl p-8 min-h-[170px] h-full">
              <BookOpen className="w-7 h-7 text-grape mb-4" />
              <div className="font-display text-xl font-bold">
                24 elite tracks
              </div>
              <p className="text-sm text-gray-400 mt-2">
                Close Protection, Cybersecurity, HEAT, Maritime, Tactical, Risk
                &amp; Compliance. Built by ex-operators, not course-mill
                teachers.
              </p>
            </div>
          </Reveal>

          <Reveal delay={300}>
            <div className="bg-ink-700 border border-white/5 rounded-3xl p-8 min-h-[170px] h-full">
              <Users className="w-7 h-7 text-lime mb-4" />
              <div className="font-display text-xl font-bold">
                Mentor-on-call
              </div>
              <p className="text-sm text-gray-400 mt-2">
                Advisory hotline with Nige and the senior instructor bench.
                Real-time Q&amp;A, live cohorts, peer review.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Outcomes */}
      <section className="relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto">
              <span className="text-xs uppercase tracking-widest text-grape font-semibold">
                Proven outcomes
              </span>
              <h2 className="font-display font-black text-4xl lg:text-5xl tracking-tight mt-3 leading-tight">
                Numbers the old-school LMS can&apos;t touch.
              </h2>
            </div>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
            {OUTCOMES.map((o, i) => (
              <Reveal key={i} delay={i * 120}>
                <div className="bg-ink-700 rounded-3xl border border-white/5 p-8 text-center h-full">
                  <div className="font-display font-black text-6xl lg:text-7xl tracking-tighter text-lime">
                    {o.big}
                  </div>
                  <div className="mt-3 font-semibold text-white">{o.small}</div>
                  <div className="mt-2 text-xs text-gray-500">{o.sub}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Instructors — "From a world you know" */}
      <section
        id="instructors"
        className="relative overflow-hidden bg-ink-800/30 border-y border-white/5"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <Reveal>
              <div>
                <span className="text-xs uppercase tracking-widest text-lime font-semibold">
                  Instructors
                </span>
                <h2 className="font-display font-black text-4xl lg:text-6xl tracking-tighter mt-3 leading-[0.95]">
                  Real <span className="text-lime">Operators</span>.<br />
                  Real <span className="text-grape italic">Experience</span>.
                </h2>
                <p className="mt-6 text-lg text-gray-400 leading-relaxed max-w-lg">
                  Every BMG instructor has carried the badge, worn the vest, or
                  sat in the SOC at 3am. Our curriculum isn&apos;t theory —
                  it&apos;s compressed experience from ex-SAS, former MI5
                  watchers, senior CISOs and UNDSS field advisors.
                </p>

                <div className="mt-8 grid grid-cols-2 gap-3">
                  {PEDIGREE.map((p, i) => {
                    const Icon = p.icon;
                    return (
                      <Reveal key={p.label} delay={120 + i * 80}>
                        <div className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-ink-700 border border-white/5">
                          <Icon className="w-4 h-4 text-lime shrink-0" />
                          <span className="text-sm font-semibold">
                            {p.label}
                          </span>
                        </div>
                      </Reveal>
                    );
                  })}
                </div>
              </div>
            </Reveal>

            <Reveal delay={120}>
              <div className="relative">
                <div className="absolute -inset-6 rounded-[2.5rem] bg-gradient-to-br from-lime/20 via-grape/15 to-transparent blur-2xl" />
                <div className="relative bg-ink-700 border border-white/10 rounded-3xl overflow-hidden">
                  <div className="aspect-[4/3] overflow-hidden bg-ink-900 flex items-center justify-center">
                    <img
                      src={NIGE_AVATAR}
                      alt="Nige Thomas"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div className="p-7">
                    <div className="text-xs uppercase tracking-widest text-lime font-semibold">
                      Lead Instructor
                    </div>
                    <div className="font-display font-black text-3xl tracking-tight mt-1">
                      Nige Thomas
                    </div>
                    <div className="text-sm text-grape font-semibold mt-1">
                      BMG Course Instructor
                    </div>
                    <p className="text-sm text-gray-400 mt-4 leading-relaxed">
                      25+ years across military CP, government security advisory
                      and corporate risk. Nige designed every flagship BMG
                      curriculum and personally delivers the capstone drills.
                    </p>
                    <div className="mt-5 flex flex-wrap gap-2">
                      {[
                        "Close Protection",
                        "HEAT",
                        "Counter-Surveillance",
                        "Active Shooter",
                      ].map((t) => (
                        <span
                          key={t}
                          className="text-[11px] font-semibold px-3 py-1 rounded-full bg-ink-800 border border-white/5 text-gray-300"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Featured courses */}
      <section
        id="courses"
        className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28"
      >
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
            <div>
              <span className="text-xs uppercase tracking-widest text-grape font-semibold">
                Trending this week
              </span>
              <h2 className="font-display font-black text-4xl lg:text-5xl mt-3 tracking-tight">
                Featured courses
              </h2>
            </div>
            <Link
              to="/catalog"
              data-testid="featured-view-all"
              className="inline-flex items-center gap-2 text-sm font-semibold text-lime hover:gap-3 transition-all"
            >
              View all 24 <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {featured.map((c, i) => (
            <Reveal key={c.id} delay={i * 100}>
              <Link
                to={`/catalog/${c.id}`}
                data-testid={`landing-course-${c.id}`}
                className="group bg-ink-700 border border-white/5 rounded-2xl overflow-hidden hover:border-white/15 transition-all hover:-translate-y-1 block h-full"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={c.thumbnail}
                    alt={c.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <div className="text-xs text-grape font-semibold mb-2">
                    {c.category}
                  </div>
                  <h3 className="font-display font-bold text-lg leading-tight line-clamp-2 group-hover:text-lime transition-colors">
                    {c.title}
                  </h3>
                  <div className="mt-4 flex items-center justify-between text-sm">
                    <span className="flex items-center gap-1 text-gray-400">
                      <Star
                        className="w-3.5 h-3.5 text-lime"
                        fill="currentColor"
                      />{" "}
                      {c.rating}
                    </span>
                    <span className="font-display font-bold text-white">
                      £{c.price}
                    </span>
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Comparison */}
      <section className="bg-ink-800/30 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <Reveal>
            <div className="text-center max-w-3xl mx-auto mb-12">
              <span className="text-xs uppercase tracking-widest text-lime font-semibold">
                Why us
              </span>
              <h2 className="font-display font-black text-4xl lg:text-5xl tracking-tight mt-3">
                Not another &ldquo;watch-and-forget&rdquo; course library.
              </h2>
            </div>
          </Reveal>
          <Reveal delay={100}>
            <div className="overflow-x-auto rounded-3xl border border-white/10 bg-ink-700">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-white/10 text-xs uppercase tracking-widest text-gray-500">
                    <th className="px-6 py-4 font-semibold">Capability</th>
                    <th className="px-6 py-4 font-semibold text-gray-400">
                      Generic LMS
                    </th>
                    <th className="px-6 py-4 font-semibold text-lime">
                      Blue Mountain
                    </th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {[
                    [
                      "Operator-led content",
                      "Academic instructors",
                      "Ex-SAS, MI5, CISO, UNDSS",
                    ],
                    [
                      "Scenario-based drills",
                      "Multiple-choice only",
                      "Scored scenarios with AAR",
                    ],
                    [
                      "Real certification",
                      "Participation certificate",
                      "BTEC/ISO-aligned, verifiable",
                    ],
                    [
                      "Corporate licensing",
                      "Per-seat add-on",
                      "Dedicated success manager",
                    ],
                    [
                      "Mentor support",
                      "Email ticket",
                      "Advisory hotline & cohorts",
                    ],
                  ].map((row, i) => (
                    <tr
                      key={i}
                      className="border-b border-white/5 last:border-0"
                    >
                      <td className="px-6 py-4 font-semibold">{row[0]}</td>
                      <td className="px-6 py-4 text-gray-500">{row[1]}</td>
                      <td className="px-6 py-4 text-white font-semibold inline-flex items-center gap-2">
                        <Check className="w-4 h-4 text-lime" /> {row[2]}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Testimonials */}
      <section
        id="testimonials"
        className="max-w-7xl mx-auto px-6 lg:px-10 py-20"
      >
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {[
            {
              name: "Jacob Jones",
              role: "CP Operator · London",
              quote:
                "Best investment in my career. The capstone drill was harder than my original CPO selection.",
              img: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=200&h=200&fit=crop&auto=format",
            },
            {
              name: "Jane Cooper",
              role: "SOC Analyst · Fortune 100",
              quote:
                "The OSINT module rewired how I triage alerts. I now spot insider threats two steps earlier.",
              img: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=200&h=200&fit=crop&auto=format",
            },
            {
              name: "Wade Warren",
              role: "Risk Advisor · NGO",
              quote:
                "HEAT felt like a deployment, not a classroom. 10/10 for pressure-testing the fundamentals.",
              img: "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=200&h=200&fit=crop&auto=format",
            },
          ].map((t, i) => (
            <Reveal key={i} delay={i * 100}>
              <div className="bg-ink-700 rounded-2xl p-7 border border-white/5 h-full">
                <div className="flex gap-1 mb-5">
                  {[...Array(5)].map((_, j) => (
                    <Star
                      key={j}
                      className="w-4 h-4 text-lime"
                      fill="currentColor"
                    />
                  ))}
                </div>
                <p className="font-display text-xl leading-snug">
                  &ldquo;{t.quote}&rdquo;
                </p>
                <div className="mt-6 flex items-center gap-3">
                  <img
                    src={t.img}
                    alt={t.name}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <div>
                    <div className="font-semibold text-sm">{t.name}</div>
                    <div className="text-xs text-gray-500">{t.role}</div>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Corporate Training Lead Form */}
      <section id="corporate" className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          <Reveal>
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-lime/15 border border-lime/30 text-lime text-xs font-semibold mb-6">
                <Building2 className="w-3.5 h-3.5" /> For Corporates
              </div>
              <h2 className="font-display font-black text-4xl lg:text-6xl tracking-tighter leading-[1] max-w-xl">
                Train your <span className="text-lime">whole team.</span>
              </h2>
              <p className="mt-6 text-lg text-gray-400 max-w-lg leading-relaxed">
                Corporate licences from 10 seats up. Bespoke curriculum design,
                dedicated mentors, on-site delivery across EMEA &amp; APAC.
              </p>
              <ul className="mt-8 space-y-3 text-sm">
                {[
                  "Volume pricing from £20,000 / cohort",
                  "Dedicated success manager and analytics dashboard",
                  "BTEC, ISO 27001 and ASIS-aligned content",
                  "On-site, virtual or hybrid delivery",
                ].map((b, i) => (
                  <li key={i} className="flex items-center gap-3 text-gray-300">
                    <span className="w-5 h-5 rounded-full bg-lime/20 flex items-center justify-center shrink-0">
                      <Check className="w-3 h-3 text-lime" />
                    </span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>

          <Reveal delay={120}>
            <div className="bg-ink-700 rounded-3xl border border-white/10 p-7 lg:p-9 relative overflow-hidden">
              <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-grape/20 blur-3xl pointer-events-none" />
              <div className="absolute -bottom-12 -left-12 w-44 h-44 rounded-full bg-lime/15 blur-3xl pointer-events-none" />
              <div className="relative">
                {submitted ? (
                  <div className="text-center py-12">
                    <div className="mx-auto w-14 h-14 rounded-full bg-lime/15 flex items-center justify-center">
                      <Check className="w-7 h-7 text-lime" />
                    </div>
                    <h3 className="font-display font-black text-2xl mt-5">
                      Request received
                    </h3>
                    <p className="text-sm text-gray-400 mt-2 max-w-sm mx-auto">
                      Our advisory team will reach out within 24 hours with a
                      tailored proposal.
                    </p>
                    <button
                      onClick={() => {
                        setSubmitted(false);
                        setLead({
                          company: "",
                          name: "",
                          email: "",
                          phone: "",
                          cohort_size: 10,
                          interest: "Close Protection",
                          message: "",
                        });
                      }}
                      className="mt-6 text-sm text-lime font-semibold hover:underline"
                      data-testid="corporate-reset"
                    >
                      Send another
                    </button>
                  </div>
                ) : (
                  <form
                    onSubmit={submitLead}
                    className="space-y-4"
                    data-testid="corporate-form"
                  >
                    <h3 className="font-display font-bold text-2xl tracking-tight mb-1">
                      Request Corporate Training
                    </h3>
                    <p className="text-sm text-gray-400 mb-3">
                      No commitment. We&apos;ll reply within one business day.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-400 mb-1.5">
                          Company *
                        </label>
                        <input
                          data-testid="lead-company"
                          required
                          value={lead.company}
                          onChange={(e) =>
                            setLead({ ...lead, company: e.target.value })
                          }
                          className="w-full bg-ink-800 border border-white/5 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-lime/40"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-400 mb-1.5">
                          Your name *
                        </label>
                        <input
                          data-testid="lead-name"
                          required
                          value={lead.name}
                          onChange={(e) =>
                            setLead({ ...lead, name: e.target.value })
                          }
                          className="w-full bg-ink-800 border border-white/5 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-lime/40"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-400 mb-1.5">
                          Work email *
                        </label>
                        <input
                          data-testid="lead-email"
                          type="email"
                          required
                          value={lead.email}
                          onChange={(e) =>
                            setLead({ ...lead, email: e.target.value })
                          }
                          className="w-full bg-ink-800 border border-white/5 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-lime/40"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-400 mb-1.5">
                          Phone
                        </label>
                        <input
                          data-testid="lead-phone"
                          value={lead.phone}
                          onChange={(e) =>
                            setLead({ ...lead, phone: e.target.value })
                          }
                          className="w-full bg-ink-800 border border-white/5 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-lime/40"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-400 mb-1.5">
                          Cohort size
                        </label>
                        <input
                          data-testid="lead-cohort"
                          type="number"
                          min={1}
                          value={lead.cohort_size}
                          onChange={(e) =>
                            setLead({
                              ...lead,
                              cohort_size: parseInt(e.target.value || "1"),
                            })
                          }
                          className="w-full bg-ink-800 border border-white/5 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-lime/40"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-400 mb-1.5">
                          Primary interest
                        </label>
                        <select
                          data-testid="lead-interest"
                          value={lead.interest}
                          onChange={(e) =>
                            setLead({ ...lead, interest: e.target.value })
                          }
                          className="w-full bg-ink-800 border border-white/5 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-lime/40"
                        >
                          {[
                            "Close Protection",
                            "Cybersecurity",
                            "Travel Safety",
                            "HEAT",
                            "Risk Management",
                            "Maritime",
                            "Tactical",
                            "Other",
                          ].map((c) => (
                            <option key={c}>{c}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1.5">
                        Message
                      </label>
                      <textarea
                        data-testid="lead-message"
                        rows={3}
                        value={lead.message}
                        onChange={(e) =>
                          setLead({ ...lead, message: e.target.value })
                        }
                        placeholder="Tell us about your team and goals…"
                        className="w-full bg-ink-800 border border-white/5 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-lime/40 resize-none"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={submitting}
                      data-testid="lead-submit"
                      className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-lime text-ink-900 font-bold hover:bg-lime-hover transition-all hover:-translate-y-0.5 disabled:opacity-50"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" /> Sending…
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4" /> Request a proposal
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-4xl mx-auto px-6 lg:px-10 py-20">
        <Reveal>
          <div className="text-center mb-10">
            <span className="text-xs uppercase tracking-widest text-lime font-semibold">
              FAQ
            </span>
            <h2 className="font-display font-black text-4xl lg:text-5xl tracking-tight mt-3">
              Straight answers.
            </h2>
          </div>
        </Reveal>
        <div className="space-y-3">
          {FAQ.map((f, i) => (
            <Reveal key={i} delay={i * 80}>
              <button
                onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                data-testid={`faq-${i}`}
                className="w-full text-left bg-ink-700 rounded-2xl border border-white/5 hover:border-white/10 p-6 transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <h3 className="font-display font-bold text-lg leading-tight">
                    {f.q}
                  </h3>
                  <span
                    className={`text-lime text-xl transition-transform ${openFaq === i ? "rotate-45" : ""}`}
                  >
                    +
                  </span>
                </div>
                {openFaq === i && (
                  <p className="text-sm text-gray-400 mt-3 leading-relaxed">
                    {f.a}
                  </p>
                )}
              </button>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-10 pb-24">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-grape/30 via-ink-700 to-lime/20 border border-white/10 p-12 lg:p-20 text-center">
            <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-lime/20 blur-3xl" />
            <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-grape/30 blur-3xl" />
            <div className="relative">
              <h2 className="font-display font-black text-4xl lg:text-6xl tracking-tighter">
                Ready to climb?
              </h2>
              <p className="mt-5 text-lg text-gray-300 max-w-xl mx-auto">
                Free preview lessons. Cancel anytime. 30-day money-back
                guarantee on every course.
              </p>
              <Link
                to="/dashboard"
                data-testid="footer-cta"
                className="mt-10 inline-flex items-center gap-2 px-8 py-4 rounded-full bg-lime text-ink-900 font-bold hover:bg-lime-hover transition-all hover:-translate-y-0.5"
              >
                Enter the app <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </Reveal>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <img
              src={LOGO_URL}
              alt="Blue Mountain Group"
              className="h-10 w-auto object-contain"
            />
          </div>
          <div className="text-xs text-gray-500">
            © 2026 Blue Mountain Group. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}







